import React, { useState, useEffect } from 'react';
import { 
    Mail, Send, Search, Filter, Clock, User, Phone, Video,
    Paperclip, Star, Archive, Trash2, Reply, Forward,
    MessageCircle, FileText, Calendar, Plus
} from 'lucide-react';

const CommunicationPanel = () => {
    const [selectedTab, setSelectedTab] = useState('inbox');
    const [selectedMessage, setSelectedMessage] = useState(null);
    const [newMessage, setNewMessage] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [showCompose, setShowCompose] = useState(false);

    const [messages, setMessages] = useState([
        {
            id: 1,
            from: 'Sarah Johnson',
            fromEmail: 'sarah.j@email.com',
            subject: 'Re: Interview Feedback - Senior Developer Position',
            preview: 'Thank you for the interview yesterday. I wanted to follow up on the timeline...',
            timestamp: '2024-01-15T10:30:00Z',
            isRead: false,
            type: 'candidate',
            candidateId: 'cand-001',
            requisitionId: 'req-001',
            priority: 'high',
            attachments: [],
            thread: [
                {
                    id: 1,
                    from: 'Sarah Johnson',
                    fromEmail: 'sarah.j@email.com',
                    content: 'Thank you for the interview yesterday. I wanted to follow up on the timeline for next steps. I remain very interested in the position and look forward to hearing from you.',
                    timestamp: '2024-01-15T10:30:00Z',
                    type: 'received'
                }
            ]
        },
        {
            id: 2,
            from: 'Mike Chen',
            fromEmail: 'mike.chen@email.com',
            subject: 'Questions about Product Manager Role',
            preview: 'Hi there, I had a few questions about the responsibilities mentioned in...',
            timestamp: '2024-01-14T15:45:00Z',
            isRead: true,
            type: 'candidate',
            candidateId: 'cand-002',
            requisitionId: 'req-002',
            priority: 'medium',
            attachments: [],
            thread: [
                {
                    id: 1,
                    from: 'Mike Chen',
                    fromEmail: 'mike.chen@email.com',
                    content: 'Hi there, I had a few questions about the responsibilities mentioned in the job posting. Could we schedule a brief call to discuss?',
                    timestamp: '2024-01-14T15:45:00Z',
                    type: 'received'
                },
                {
                    id: 2,
                    from: 'You',
                    fromEmail: 'recruiter@company.com',
                    content: 'Hi Mike, I\'d be happy to discuss the role in more detail. Let me send you a calendar link for this week.',
                    timestamp: '2024-01-14T16:15:00Z',
                    type: 'sent'
                }
            ]
        },
        {
            id: 3,
            from: 'John Smith (Hiring Manager)',
            fromEmail: 'john.smith@company.com',
            subject: 'Interview Panel Feedback - Emily Rodriguez',
            preview: 'The team was impressed with Emily\'s presentation. Here\'s the consolidated feedback...',
            timestamp: '2024-01-13T11:20:00Z',
            isRead: true,
            type: 'internal',
            candidateId: 'cand-003',
            requisitionId: 'req-001',
            priority: 'high',
            attachments: [
                { name: 'Interview_Feedback_Emily.pdf', size: '245 KB' }
            ]
        }
    ]);

    const [emailTemplates] = useState([
        {
            id: 1,
            name: 'Interview Invitation',
            subject: 'Interview Invitation - {{job_title}} Position',
            body: 'Dear {{candidate_name}},\n\nWe are excited to invite you for an interview for the {{job_title}} position...'
        },
        {
            id: 2,
            name: 'Thank You & Next Steps',
            subject: 'Thank you for your interview - {{job_title}}',
            body: 'Dear {{candidate_name}},\n\nThank you for taking the time to interview with us today...'
        },
        {
            id: 3,
            name: 'Position Update',
            subject: 'Update on your application - {{job_title}}',
            body: 'Dear {{candidate_name}},\n\nI wanted to provide you with an update on your application...'
        }
    ]);

    const formatTimestamp = (timestamp) => {
        const date = new Date(timestamp);
        const now = new Date();
        const diffHours = Math.abs(now - date) / 36e5;
        
        if (diffHours < 24) {
            return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
        } else if (diffHours < 168) { // 7 days
            return date.toLocaleDateString([], { weekday: 'short' });
        } else {
            return date.toLocaleDateString([], { month: 'short', day: 'numeric' });
        }
    };

    const MessageList = () => (
        <div className="divide-y divide-gray-200">
            {messages
                .filter(message => 
                    !searchTerm || 
                    message.subject.toLowerCase().includes(searchTerm.toLowerCase()) ||
                    message.from.toLowerCase().includes(searchTerm.toLowerCase())
                )
                .map((message) => (
                    <div 
                        key={message.id} 
                        className={`p-4 hover:bg-gray-50 cursor-pointer ${!message.isRead ? 'bg-blue-50' : ''}`}
                        onClick={() => setSelectedMessage(message)}
                    >
                        <div className="flex items-start justify-between">
                            <div className="flex items-start space-x-3 flex-1">
                                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                                    message.type === 'candidate' ? 'bg-blue-100 text-blue-600' :
                                    message.type === 'internal' ? 'bg-green-100 text-green-600' :
                                    'bg-gray-100 text-gray-600'
                                }`}>
                                    <User className="h-5 w-5" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between">
                                        <p className={`text-sm ${!message.isRead ? 'font-semibold' : 'font-medium'} text-gray-900 truncate`}>
                                            {message.from}
                                        </p>
                                        <div className="flex items-center space-x-2">
                                            {message.priority === 'high' && (
                                                <Star className="h-4 w-4 text-red-500" />
                                            )}
                                            <span className="text-xs text-gray-500">
                                                {formatTimestamp(message.timestamp)}
                                            </span>
                                        </div>
                                    </div>
                                    <p className={`text-sm ${!message.isRead ? 'font-medium' : ''} text-gray-900 truncate mt-1`}>
                                        {message.subject}
                                    </p>
                                    <p className="text-sm text-gray-500 truncate mt-1">
                                        {message.preview}
                                    </p>
                                    {message.attachments && message.attachments.length > 0 && (
                                        <div className="flex items-center mt-2">
                                            <Paperclip className="h-3 w-3 text-gray-400 mr-1" />
                                            <span className="text-xs text-gray-400">{message.attachments.length} attachment(s)</span>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                ))
            }
        </div>
    );

    const MessageDetail = ({ message }) => (
        <div className="h-full flex flex-col">
            <div className="p-6 border-b border-gray-200">
                <div className="flex items-center justify-between mb-4">
                    <button 
                        onClick={() => setSelectedMessage(null)}
                        className="text-blue-600 hover:text-blue-700 text-sm font-medium"
                    >
                        ← Back to Inbox
                    </button>
                    <div className="flex items-center space-x-2">
                        <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                            <Reply className="h-4 w-4" />
                        </button>
                        <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                            <Forward className="h-4 w-4" />
                        </button>
                        <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                            <Archive className="h-4 w-4" />
                        </button>
                        <button className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
                            <Trash2 className="h-4 w-4" />
                        </button>
                    </div>
                </div>
                <h2 className="text-xl font-semibold text-gray-900">{message.subject}</h2>
            </div>

            <div className="flex-1 overflow-y-auto p-6">
                <div className="space-y-6">
                    {message.thread && message.thread.map((msg, index) => (
                        <div key={index} className={`flex ${msg.type === 'sent' ? 'justify-end' : 'justify-start'}`}>
                            <div className={`max-w-3xl ${
                                msg.type === 'sent' 
                                    ? 'bg-blue-600 text-white' 
                                    : 'bg-gray-100 text-gray-900'
                            } rounded-lg p-4`}>
                                <div className="flex items-center justify-between mb-2">
                                    <span className={`text-sm font-medium ${
                                        msg.type === 'sent' ? 'text-blue-100' : 'text-gray-600'
                                    }`}>
                                        {msg.from}
                                    </span>
                                    <span className={`text-xs ${
                                        msg.type === 'sent' ? 'text-blue-200' : 'text-gray-500'
                                    }`}>
                                        {formatTimestamp(msg.timestamp)}
                                    </span>
                                </div>
                                <p className="text-sm leading-relaxed whitespace-pre-line">
                                    {msg.content}
                                </p>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            <div className="p-6 border-t border-gray-200">
                <div className="flex items-end space-x-3">
                    <div className="flex-1">
                        <textarea
                            value={newMessage}
                            onChange={(e) => setNewMessage(e.target.value)}
                            placeholder="Type your reply..."
                            className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                            rows={3}
                        />
                    </div>
                    <button className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center">
                        <Send className="h-4 w-4 mr-2" />
                        Reply
                    </button>
                </div>
            </div>
        </div>
    );

    const TemplatesPanel = () => (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold">Email Templates</h3>
                <button className="flex items-center px-3 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                    <Plus className="h-4 w-4 mr-2" />
                    New Template
                </button>
            </div>
            
            <div className="grid gap-4">
                {emailTemplates.map((template) => (
                    <div key={template.id} className="bg-white p-4 border border-gray-200 rounded-lg hover:shadow-md transition-shadow">
                        <h4 className="font-medium text-gray-900 mb-2">{template.name}</h4>
                        <p className="text-sm text-gray-600 mb-2">{template.subject}</p>
                        <p className="text-sm text-gray-500 line-clamp-2">{template.body}</p>
                        <div className="flex items-center justify-between mt-3">
                            <span className="text-xs text-gray-400">Last updated: 2 days ago</span>
                            <div className="flex space-x-2">
                                <button className="text-blue-600 hover:text-blue-700 text-sm">Use Template</button>
                                <button className="text-gray-600 hover:text-gray-700 text-sm">Edit</button>
                            </div>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );

    return (
        <div className="h-full flex flex-col">
            {/* Communication Header */}
            <div className="bg-white p-6 rounded-lg shadow-sm border mb-6">
                <div className="flex items-center justify-between mb-4">
                    <h3 className="text-lg font-semibold">Communication Center</h3>
                    <div className="flex items-center space-x-3">
                        <button 
                            onClick={() => setShowCompose(true)}
                            className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                        >
                            <Mail className="h-4 w-4 mr-2" />
                            Compose
                        </button>
                    </div>
                </div>

                {/* Tabs */}
                <div className="border-b border-gray-200">
                    <nav className="flex space-x-8">
                        {[
                            { id: 'inbox', name: 'Inbox', count: 12 },
                            { id: 'sent', name: 'Sent', count: 0 },
                            { id: 'templates', name: 'Templates', count: emailTemplates.length },
                            { id: 'scheduled', name: 'Scheduled', count: 2 }
                        ].map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setSelectedTab(tab.id)}
                                className={`flex items-center py-2 px-1 text-sm font-medium border-b-2 ${
                                    selectedTab === tab.id
                                        ? 'border-blue-500 text-blue-600'
                                        : 'border-transparent text-gray-500 hover:text-gray-700'
                                }`}
                            >
                                {tab.name}
                                {tab.count > 0 && (
                                    <span className={`ml-2 px-2 py-0.5 text-xs rounded-full ${
                                        selectedTab === tab.id
                                            ? 'bg-blue-100 text-blue-600'
                                            : 'bg-gray-100 text-gray-600'
                                    }`}>
                                        {tab.count}
                                    </span>
                                )}
                            </button>
                        ))}
                    </nav>
                </div>
            </div>

            {/* Communication Content */}
            <div className="flex-1 bg-white rounded-lg shadow-sm border overflow-hidden">
                {selectedTab === 'inbox' && !selectedMessage && (
                    <div className="h-full flex flex-col">
                        {/* Search & Filters */}
                        <div className="p-4 border-b border-gray-200">
                            <div className="flex items-center space-x-3">
                                <div className="flex-1 relative">
                                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                                    <input
                                        type="text"
                                        placeholder="Search messages..."
                                        value={searchTerm}
                                        onChange={(e) => setSearchTerm(e.target.value)}
                                        className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                                    />
                                </div>
                                <button className="flex items-center px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50">
                                    <Filter className="h-4 w-4 mr-2" />
                                    Filter
                                </button>
                            </div>
                        </div>

                        {/* Message List */}
                        <div className="flex-1 overflow-y-auto">
                            <MessageList />
                        </div>
                    </div>
                )}

                {selectedTab === 'inbox' && selectedMessage && (
                    <MessageDetail message={selectedMessage} />
                )}

                {selectedTab === 'templates' && (
                    <div className="p-6">
                        <TemplatesPanel />
                    </div>
                )}

                {(selectedTab === 'sent' || selectedTab === 'scheduled') && (
                    <div className="p-8 text-center">
                        <MessageCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                        <h3 className="text-lg font-medium text-gray-900 mb-2">
                            {selectedTab === 'sent' ? 'Sent Messages' : 'Scheduled Messages'}
                        </h3>
                        <p className="text-gray-600">
                            {selectedTab === 'sent' 
                                ? 'Your sent messages will appear here.' 
                                : 'Your scheduled messages will appear here.'
                            }
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default CommunicationPanel;